import { useCallback, useEffect, useMemo } from "react";

import { Flex, IconButton, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate, useBlocker } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { PageName } from "analytics/events";
import { routes } from "config/routes";
import { DiscardBagConfirmationModal } from "flows/Inbound/components/DiscardBagConfirmationModal";
import { ProductsSearchComponent } from "flows/Inbound/components/ProductsSearchComponent";
import { ScanPlaceholder } from "flows/Inventory/shared/components/ScanPlaceholder";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useScan } from "shared/hooks/useScan/useScan";
import { IntlMessageId } from "shared/lang";
import {
  ProductSearchActionTypes,
  useProductSearchStore,
} from "shared/stores/useProductSearchStore";
import { InfoCircleIcon } from "ui/Icons/Icons";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";

import { EndPreparationButton } from "../components/EndPreparationButton";
import { GuidelinesBottomSheet } from "../components/GuidelinesBottomSheet";
import { OverGoalWarningModal } from "../components/OverGoalWarningModal";
import { OverMaximumWarningModal } from "../components/OverMaximumWarningModal";
import { ProductInfoCard } from "../components/ProductInfoCard";
import { ProductNotEligibleModal } from "../components/ProductNotEligibleModal";
import ProductNotListedModal from "../components/ProductNotListedModal";
import { useFetchProductsByEanWithPrice } from "../hooks/useFetchProductsByEan";
import { useSurpriseBagMutation } from "../hooks/useSurpriseBagMutation";
import { useTGTGBagStore } from "../stores/useTGTGBagStore";
import { calculatePriceExceedance, isOverPriceLimit } from "../utils/helpers";

export function TGTGPrepareBagPage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { sendSegmentTrackEvent } = useAnalytics();

  const {
    isOpen: isDiscardBagConfirmationModalOpen,
    onOpen: openDiscardBagConfirmationModal,
    onClose: closeDiscardBagConfirmationModal,
  } = useDisclosure();
  const {
    isOpen: isOverGoalWarningModalOpen,
    onOpen: openOverGoalWarningModal,
    onClose: closeOverGoalWarningModal,
  } = useDisclosure();
  const {
    isOpen: isOverMaximumWarningModalOpen,
    onOpen: openOverMaximumWarningModal,
    onClose: closeOverMaximumWarningModal,
  } = useDisclosure();

  const {
    bagId,
    bagKey,
    bagSku,
    minimumValue,
    totalPrice,
    maximumThreshold,
    maximumUnitsPerSku,
    minimumDistinctSkus,
    products,
    productsMap,
    showProductNotListedModal,
    showProductNotEligibleModal,
    hasShownGuidelines,
    setHasShownGuidelines,
    reset,
  } = useTGTGBagStore(
    useShallow((state) => ({
      bagId: state.bagId,
      bagKey: state.bagKey,
      bagSku: state.bagSku,
      minimumValue: state.minimumValue,
      maximumThreshold: state.maximumThreshold,
      maximumUnitsPerSku: state.maximumUnitsPerSku,
      minimumDistinctSkus: state.minimumDistinctSkus,
      totalPrice: state.totalPrice,
      products: state.products,
      productsMap: state.productsMap,
      showProductNotListedModal: state.showProductNotListedModal,
      showProductNotEligibleModal: state.showProductNotEligibleModal,
      hasShownGuidelines: state.hasShownGuidelines,
      setHasShownGuidelines: state.setHasShownGuidelines,
      reset: state.reset,
    })),
  );
  useEffect(() => {
    if (!bagKey) {
      navigate(routes.inventory.tooGoodToGo.root);
    }
  }, [bagKey, navigate]);

  const hasNewGuidelineValues = maximumUnitsPerSku != null || minimumDistinctSkus != null;

  const {
    isOpen: isBottomSheetOpen,
    onOpen: openBottomSheet,
    onClose: closeBottomSheet,
  } = useDisclosure();

  useEffect(() => {
    if (hasNewGuidelineValues && !hasShownGuidelines) {
      openBottomSheet();
      setHasShownGuidelines();
    }
  }, [hasNewGuidelineValues, hasShownGuidelines, openBottomSheet, setHasShownGuidelines]);

  const overMaximum = useMemo(
    () => isOverPriceLimit(minimumValue, totalPrice, maximumThreshold),
    [maximumThreshold, minimumValue, totalPrice],
  );

  const { maxAllowedTotal, exceedAmount } = calculatePriceExceedance(
    overMaximum,
    minimumValue,
    totalPrice,
    maximumThreshold,
  );

  useEffect(() => {
    if (overMaximum) {
      openOverMaximumWarningModal();
    }
  }, [openOverMaximumWarningModal, overMaximum]);

  useEffect(() => {
    if (isOverMaximumWarningModalOpen) {
      sendSegmentTrackEvent("inAppMessageShown", {
        screen_name: PageName.BAG_PREPARATION,
        component_name: "bag_over_maximum",
        component_content: bagId!,
      });
    }
  }, [bagId, isOverMaximumWarningModalOpen, sendSegmentTrackEvent]);

  const { onScan, isScannedProductLoading } = useFetchProductsByEanWithPrice();
  const setSearchState = useProductSearchStore((state) => state.setSearchState);

  const blockerFunction = (args: any) =>
    products.length > 0 && !args.nextLocation.pathname.includes("/inventory/too-good-to-go");
  const blocker = useBlocker(blockerFunction);
  const handleConfirmNavigation = () => {
    if (isDiscardBagConfirmationModalOpen) {
      closeDiscardBagConfirmationModal();
      navigate(routes.inventory.tooGoodToGo.root);
    } else {
      blocker.proceed?.();
    }
    reset();
  };
  const handleCancelNavigation = () => {
    blocker.reset?.();
    closeDiscardBagConfirmationModal();
  };
  const activateSearchFlow = useCallback(() => {
    setSearchState(ProductSearchActionTypes.ACTIVATE_SEARCH);
    navigate(routes.inventory.tooGoodToGo.selectProduct);
  }, [navigate, setSearchState]);

  useScan({ onScan });

  const calculatePrices = useMemo(() => {
    const bagPrice = Math.round((minimumValue ?? 0) * 100) / 100;
    const currentTotalPrice = Math.round(totalPrice * 100) / 100;
    return { bagPrice, currentTotalPrice };
  }, [minimumValue, totalPrice]);

  const { updateSurpriseBagStock, loading } = useSurpriseBagMutation();
  const handleBagState = useCallback(
    (isOverGoal: boolean) => {
      if (isOverGoal) {
        openOverGoalWarningModal();
        sendSegmentTrackEvent("inAppMessageShown", {
          screen_name: PageName.BAG_PREPARATION,
          component_name: "bag_over_the_goal",
          component_content: bagId!,
        });
        return;
      }
      if (loading) return;
      updateSurpriseBagStock({
        bagId,
        bagSku,
        bagKey,
        maximumThreshold,
        productsMap,
        calculatePrices,
      });
    },
    [
      bagId,
      bagKey,
      bagSku,
      calculatePrices,
      loading,
      maximumThreshold,
      openOverGoalWarningModal,
      productsMap,
      sendSegmentTrackEvent,
      updateSurpriseBagStock,
    ],
  );

  const handleEndPreparationButton = useCallback(() => {
    const THRESHOLD = 2;
    const { bagPrice, currentTotalPrice } = calculatePrices;
    const targetPrice = bagPrice + THRESHOLD;
    const isOverGoal = currentTotalPrice > targetPrice;
    handleBagState(isOverGoal);
  }, [calculatePrices, handleBagState]);

  const handleOverGoalWarningConfirm = useCallback(() => {
    handleBagState(false);
    closeOverGoalWarningModal();
  }, [closeOverGoalWarningModal, handleBagState]);

  return (
    <Page isFull isBgGrey h="100%" justifyContent="flex-start">
      <NavigationHeader
        title={intl.formatMessage({
          id: `pages.tgtg-bag-selection-page.bag-types.${bagKey}` as IntlMessageId,
        })}
        onClickGoBack={() =>
          products.length > 0
            ? openDiscardBagConfirmationModal()
            : navigate(routes.inventory.tooGoodToGo.root)
        }
        rightElement={
          hasNewGuidelineValues ? (
            <IconButton
              aria-label="close"
              size="xs"
              variant="ghost"
              icon={<InfoCircleIcon boxSize="1.5rem" />}
              onClick={openBottomSheet}
            />
          ) : undefined
        }
      />
      <ProductsSearchComponent
        isSearchFlowActive={false}
        activateTextSearch={activateSearchFlow}
        showUnitsToggle={false}
      />
      {!products.length ? (
        <ScanPlaceholder
          title="pages.tgtg-bag-selection-page.scan-placeholder.title"
          subtitle="pages.tgtg-bag-selection-page.scan-placeholder.subtitle"
          justifyContent="center"
          flexGrow="1"
          px="s400"
        />
      ) : (
        <Flex
          direction="column"
          w="100%"
          p="s100"
          marginBottom="72px"
          gap="s200"
          pb="s300"
          overflow="scroll"
          css={{
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {products.map((productSku: string) => (
            <ProductInfoCard key={productSku} productSku={productSku} />
          ))}
        </Flex>
      )}
      <EndPreparationButton
        bagOfPrice={minimumValue ?? 0}
        totalPrice={totalPrice}
        maximumThreshold={maximumThreshold}
        minimumDistinctSkus={minimumDistinctSkus}
        distinctSkusCount={products.length}
        onClickButton={handleEndPreparationButton}
      />
      <DiscardBagConfirmationModal
        isOpen={blocker.state === "blocked" || isDiscardBagConfirmationModalOpen}
        onClickYes={handleConfirmNavigation}
        onClickNo={handleCancelNavigation}
      />
      <ProductNotListedModal isOpen={showProductNotListedModal} />
      <ProductNotEligibleModal isOpen={showProductNotEligibleModal} />
      {isOverGoalWarningModalOpen && (
        <OverGoalWarningModal
          onClickYes={handleOverGoalWarningConfirm}
          onClickNo={closeOverGoalWarningModal}
        />
      )}
      {isOverMaximumWarningModalOpen && (
        <OverMaximumWarningModal
          exceedAmount={exceedAmount}
          maxAllowedTotal={maxAllowedTotal}
          onClose={closeOverMaximumWarningModal}
        />
      )}
      <SpinnerModal isOpen={isScannedProductLoading} />
      <GuidelinesBottomSheet
        isBottomSheetOpen={isBottomSheetOpen}
        closeBottomSheet={closeBottomSheet}
      />
    </Page>
  );
}
